/**
 * Fabricating customer threads for one scenario type. Each thread streams in as a preview under its
 * type, and the saved rows replace the previews once the stream ends; a refusal stays with the type's
 * note. A stream keeps the SOP it was started on, since the panel stays mounted when another protocol opens.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '@/components/ui/sonner';
import { reloadBackTestingTypes } from '@/hooks/use-back-testing-types';
import { aiFunctionService, type FabricatedPreview } from '@/services/ai-function.service';
import { counted } from '../../format';
import { toLocalScenario, type Scenario } from '../../types';
import { refusalOf } from '../refusal';
import type { HeldRefusal } from '../use-held-refusal';

export interface FabricationRequest {
	count: number;
	/** What the fabricated customers should be like; null leaves it to the type. */
	brief: string | null;
}

interface Stream {
	token: number;
	sopId: string;
	typeId: string;
	previews: FabricatedPreview[];
}

export const useFabrication = (sopId: string, { hold, dismiss }: Pick<HeldRefusal, 'hold' | 'dismiss'>) => {
	const [stream, setStream] = useState<Stream | null>(null);
	const tokenRef = useRef(0);
	const sopOnScreen = useRef(sopId);
	useEffect(() => {
		sopOnScreen.current = sopId;
	}, [sopId]);

	const fabricate = useCallback(
		async (
			typeId: string,
			{ count, brief }: FabricationRequest,
			onSaved: (sopId: string, scenarios: Scenario[]) => void
		) => {
			const token = ++tokenRef.current;
			const forSop = sopId;
			setStream({ token, sopId: forSop, typeId, previews: [] });
			dismiss();
			try {
				const { scenarios } = await aiFunctionService.streamFabricatedScenarios(
					{ sopId: forSop, scenarioTypeId: typeId, count, ...(brief ? { brief } : {}) },
					{
						onElement: (preview) =>
							setStream((prev) => (prev?.token === token ? { ...prev, previews: [...prev.previews, preview] } : prev))
					}
				);
				if (!scenarios.length) {
					hold({ sopId: forSop, typeId }, { message: 'No scenarios came back', datasets: [] });
					return;
				}
				onSaved(forSop, scenarios.map((scenario) => toLocalScenario(forSop, scenario)));
				if (sopOnScreen.current === forSop) toast.success(`${counted(scenarios.length, 'scenario')} fabricated`);
			} catch (err) {
				hold({ sopId: forSop, typeId }, refusalOf(err, 'Could not fabricate scenarios'));
			} finally {
				setStream((prev) => (prev?.token === token ? null : prev));
				// The counts decide the cap, so they are read again whether the server took the request or refused it.
				void reloadBackTestingTypes(forSop);
			}
		},
		[sopId, hold, dismiss]
	);

	const own = stream?.sopId === sopId ? stream : null;
	return { fabricatingTypeId: own?.typeId ?? null, previews: own?.previews ?? [], fabricate };
};
